/**
 * XLSX eksporti — jadval ustunlarini Excel faylga.
 *
 * KUTUBXONA DINAMIK YUKLANADI (`await import('xlsx')`). SheetJS ~400 KB —
 * uni asosiy bundle'ga qo'shish har bir sahifa ochilishini sekinlashtirardi,
 * holbuki eksport kuniga bir necha marta bosiladi. Dinamik import bilan u
 * ALOHIDA chunk bo'ladi va faqat tugma bosilganda yuklanadi.
 */

/** Bitta ustun: qanday nomlanadi va qatordan qiymat qanday olinadi. */
export interface Ustun<T> {
  key: string;
  label: string;
  /** Murakkab qiymat uchun (ichma-ich obyekt, formatlash). Berilmasa `row[key]`. */
  get?: (row: T) => unknown;
}

/** Excel katakchasiga tushadigan qiymatga aylantirish. */
function qiymat(v: unknown): string | number | boolean | Date | null {
  if (v == null) return null;
  if (v instanceof Date) return v;
  if (typeof v === 'number' || typeof v === 'boolean') return v;
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  // ISO sana — Excel uni haqiqiy sana sifatida tanisin (saralash va filtr ishlasin)
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) {
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d;
  }
  return s;
}

/** Sana maydonidan chorak kaliti: "2026-Q1". */
export function chorakKaliti(v: unknown): string | null {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(String(v));
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
}

/** Excel varaq nomi uchun cheklovlar: ≤31 belgi va `: \ / ? * [ ]` mumkin emas. */
function varaqNomi(s: string): string {
  return s.replace(/[:\\/?*[\]]/g, '-').slice(0, 31) || 'Sheet';
}

/** Ustun kengligini mazmunga moslash — hamma katakcha "###" bo'lib qolmasin. */
function kengliklar<T>(ustunlar: Ustun<T>[], qatorlar: unknown[][]): { wch: number }[] {
  return ustunlar.map((u, i) => {
    let max = u.label.length;
    for (const r of qatorlar) {
      const v = r[i];
      const n = v instanceof Date ? 16 : String(v ?? '').length;
      if (n > max) max = n;
    }
    return { wch: Math.min(50, Math.max(8, max + 2)) };
  });
}

export interface EksportParam<T> {
  filename: string;
  ustunlar: Ustun<T>[];
  qatorlar: T[];
  /**
   * Sana maydoni. Berilsa va qatorlar ko'p bo'lsa, fayl CHORAKLARGA bo'linadi:
   * har chorak — alohida varaq. Bitta varaqda o'n minglab qator bo'lsa, Excel
   * uni sekin ochadi va unda ishlash amalda imkonsiz bo'ladi.
   */
  sanaMaydoni?: (row: T) => unknown;
  /** Shu chegaradan ko'p qator bo'lsa choraklarga bo'linadi. */
  chegara?: number;
}

const CHEGARA = 3000;

export async function eksportXlsx<T>({
  filename,
  ustunlar,
  qatorlar,
  sanaMaydoni,
  chegara = CHEGARA,
}: EksportParam<T>): Promise<{ varaqlar: number; qatorlar: number }> {
  const XLSX = await import('xlsx');
  const kitob = XLSX.utils.book_new();

  const varaqQo = (nom: string, rows: T[]) => {
    const matritsa = rows.map((r) => ustunlar.map((u) => qiymat(u.get ? u.get(r) : (r as Record<string, unknown>)[u.key])));
    const varaq = XLSX.utils.aoa_to_sheet([ustunlar.map((u) => u.label), ...matritsa]);
    varaq['!cols'] = kengliklar(ustunlar, matritsa);
    // Sarlavha qatori doim ko'rinib tursin (uzun ro'yxatda aylantirganda)
    varaq['!freeze'] = { xSplit: 0, ySplit: 1 };
    XLSX.utils.book_append_sheet(kitob, varaq, varaqNomi(nom));
  };

  const boluvchi = sanaMaydoni && qatorlar.length > chegara;
  if (boluvchi) {
    /* CHORAKLARGA BO'LISH. Guruhlash tartibi saqlanadi (eng yangisi birinchi
       varaq) — odatda oxirgi chorak kerak bo'ladi, uni qidirib yurmaslik uchun. */
    const guruh = new Map<string, T[]>();
    for (const r of qatorlar) {
      const k = chorakKaliti(sanaMaydoni!(r)) ?? 'Sanasiz';
      const a = guruh.get(k);
      if (a) a.push(r);
      else guruh.set(k, [r]);
    }
    const kalitlar = [...guruh.keys()].sort().reverse();
    for (const k of kalitlar) varaqQo(k, guruh.get(k)!);
  } else {
    varaqQo('Hammasi', qatorlar);
  }

  XLSX.writeFile(kitob, `${filename}.xlsx`, { compression: true });
  return { varaqlar: kitob.SheetNames.length, qatorlar: qatorlar.length };
}
